// =============================================================================
// FILE: packages/renderer/src/components/community/CommunityPartners.tsx
// PURPOSE: "Community Partners" section. Third-party logos are NEVER shown by
//   default — each partner is a reserved placeholder. A partner representative
//   can click a tile to sign brand consent (BrandConsentModal); once the founder
//   approves it in the Command Centre, that organization's logo is displayed.
// =============================================================================

import { useEffect, useState, type JSX } from 'react';
import { BrandConsentModal } from './BrandConsentModal.js';

interface Partner { name: string; sub: string; href: string; }

const BACKEND: string = (() => {
  const w = (typeof window !== 'undefined'
    ? (window as unknown as Record<string, unknown>).__BACKEND_URL__
    : undefined) as string | undefined;
  return w || ((import.meta.env.VITE_BACKEND_URL as string) ?? '');
})();

const PARTNERS: Partner[] = [
  { name: 'Couples for Christ', sub: 'Family & Life', href: 'https://couplesforchristglobal.org/' },
  { name: 'ANCOP Canada', sub: 'Answering the Cry of the Poor', href: 'https://ancopcanada.org/' },
  { name: "Catholic Women's League", sub: 'For God and Canada', href: 'https://cwl.ca' },
];

function slugify(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}
function initials(name: string): string {
  return name.replace(/[^A-Za-z ]/g, '').split(/\s+/).filter(Boolean).map((w) => w[0]!.toUpperCase()).slice(0, 3).join('');
}
function logoSrc(logo: string): string { return logo.startsWith('http') ? logo : `${BACKEND}${logo}`; }

export function CommunityPartners(): JSX.Element {
  const [logos, setLogos] = useState<Record<string, string>>({});
  const [active, setActive] = useState<Partner | null>(null);

  const loadLogos = async (): Promise<void> => {
    try {
      const r = await fetch(`${BACKEND}/api/partners/consent/public`, { signal: AbortSignal.timeout(6000) });
      if (!r.ok) return;
      const d = await r.json() as { partners: { org_slug: string; logo: string | null }[] };
      const map: Record<string, string> = {};
      for (const p of d.partners) if (p.logo) map[p.org_slug] = p.logo;
      setLogos(map);
    } catch { /* keep placeholders */ }
  };
  useEffect(() => { void loadLogos(); }, []);

  return (
    <section data-testid="community-partners" style={{ marginTop: 28, paddingTop: 22, borderTop: '1px solid rgba(63,63,70,0.4)' }}>
      <div style={{ textAlign: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 10, letterSpacing: '0.24em', textTransform: 'uppercase', color: 'rgba(37,181,168,0.85)' }}>Community Partners</div>
        <div style={{ fontSize: 12, color: 'rgba(148,172,210,0.6)', marginTop: 5 }}>
          Built with purpose — in support of faith, family and service.
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, alignItems: 'stretch' }}>
        {PARTNERS.map((p) => {
          const slug = slugify(p.name);
          const logo = logos[slug];
          return (
            <button
              key={p.name}
              type="button"
              data-testid={`partner-${slug}`}
              onClick={() => setActive(p)}
              title={`${p.name} — click to manage brand consent`}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 8,
                padding: '14px 10px', borderRadius: 12, cursor: 'pointer',
                background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(148,163,184,0.18)',
                transition: 'border-color 0.2s, background 0.2s',
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(37,181,168,0.5)'; (e.currentTarget as HTMLElement).style.background = 'rgba(37,181,168,0.06)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(148,163,184,0.18)'; (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.04)'; }}
            >
              {logo ? (
                <span style={{ width: 52, height: 52, borderRadius: 10, display: 'grid', placeItems: 'center', overflow: 'hidden', background: 'rgba(255,255,255,0.9)' }}>
                  <img src={logoSrc(logo)} alt={`${p.name} logo`} data-testid={`partner-logo-${slug}`} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                </span>
              ) : (
                <span style={{ width: 48, height: 48, borderRadius: 10, display: 'grid', placeItems: 'center', background: 'rgba(37,181,168,0.08)', border: '1px dashed rgba(37,181,168,0.4)' }}>
                  <span style={{ fontSize: 14, fontWeight: 800, letterSpacing: '0.06em', color: 'rgba(94,234,212,0.9)' }}>{initials(p.name)}</span>
                </span>
              )}
              <span>
                <span style={{ display: 'block', fontSize: 11.5, fontWeight: 700, color: 'rgb(226,232,240)', lineHeight: 1.2 }}>{p.name}</span>
                <span style={{ display: 'block', fontSize: 10, color: 'rgba(148,172,210,0.6)', marginTop: 2 }}>{p.sub}</span>
                <span style={{ display: 'block', fontSize: 8.5, textTransform: 'uppercase', letterSpacing: '0.14em', color: logo ? 'rgba(94,234,212,0.65)' : 'rgba(148,172,210,0.45)', marginTop: 4 }}>
                  {logo ? '✓ Consent on file' : 'Manage brand consent'}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      <p data-testid="community-partners-note" style={{ textAlign: 'center', fontSize: 10, lineHeight: 1.5, color: 'rgba(148,172,210,0.5)', marginTop: 12, maxWidth: 540, marginLeft: 'auto', marginRight: 'auto' }}>
        These organizations are honored community partners. Their brand marks appear here only after an authorized
        representative signs consent — click a tile to review, approve, or decline. A reserved placeholder is shown until then.
      </p>

      {active && (
        <BrandConsentModal
          org={active.name}
          orgSlug={slugify(active.name)}
          onClose={() => setActive(null)}
          onSubmitted={() => { void loadLogos(); }}
        />
      )}
    </section>
  );
}
